import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Flex, Typography, TypographyVariants } from "@aircall/tractor";
import { EditableSelect, EditableTextarea } from "@dashboard/library";

import {
  CustomSummaryResultFragment,
  SummaryTemplateResult,
} from "@/graphql/generated/graphql";

type SummaryTemplate =
  CustomSummaryResultFragment["customSummary"]["templates"][number]["template"];

type CustomSummaryResultsFormFieldSelectProps = {
  content: string;
  template?: SummaryTemplate;
  onUpdate: (newValue: string) => void;
};

function CustomSummaryResultsFormFieldSelect({
  content,
  template,
  onUpdate,
}: CustomSummaryResultsFormFieldSelectProps) {
  const { t } = useTranslation();

  const options = useMemo(() => {
    if (!template?.response) return [];

    if (template.response.__typename === "OptionsResponse") {
      return template.response.options.map((option) => ({
        label: option.value,
        value: option.value,
      }));
    }

    if (template.response.__typename === "ValidationResponse") {
      return template.response.validationOptions.map((option) => ({
        label: t(`custom_summary.validation_options.${option.value}`),
        value: option.value,
      }));
    }

    return [];
  }, [template, t]);

  return (
    <EditableSelect<string>
      defaultValue={content ?? undefined}
      placeholder={t("call_context.summary_empty_message")}
      onChange={onUpdate}
      options={options}
    />
  );
}

type CustomSummaryResultsFormFieldProps = {
  templateResult: SummaryTemplateResult;
  template?: SummaryTemplate;
  onUpdate: (newValue: string) => void;
  variants: {
    title: TypographyVariants;
    field: TypographyVariants;
  };
  readOnly?: boolean;
};

export function CustomSummaryResultsFormField({
  template,
  templateResult,
  onUpdate,
  variants,
  readOnly = false,
}: CustomSummaryResultsFormFieldProps) {
  const { t } = useTranslation();
  const { title: titleVariant, field: fieldVariant } = variants;
  const { summaryTemplateName, content, type } = templateResult;

  return (
    <Flex flexDirection="column" gap={2}>
      <Typography variant={titleVariant} color="text-highlight">
        {summaryTemplateName}
      </Typography>

      {readOnly ? (
        <Typography
          variant={fieldVariant}
          color="text-base"
          whiteSpace="pre-line"
        >
          {content}
        </Typography>
      ) : (
        <>
          {type === "DISCOVERY" ? (
            <EditableTextarea
              placeholder={t("PlaybookSummary.Empty")}
              defaultValue={content ?? undefined}
              onSubmit={(newValue) => onUpdate(newValue as string)}
              variant={fieldVariant}
              maxLength={1024}
            />
          ) : (
            <CustomSummaryResultsFormFieldSelect
              content={content}
              template={template}
              onUpdate={onUpdate}
            />
          )}
        </>
      )}
    </Flex>
  );
}
